import BusinessIcon from "@mui/icons-material/Business";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PersonIcon from "@mui/icons-material/Person";
import { IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import { Dayjs } from "dayjs";
import { Check, LucideX } from "lucide-react";
import { SetStateAction, useEffect, useState } from "react";
import { useUser } from "../../Contexts/UserContext/UserContext";
import { groupBy } from "../../Hooks/Utilities/Utlitites";
import { ITask, ITaskDTO } from "../../Interfaces/Task";
import { User } from "../../Interfaces/User";
import { Input } from "../UI/Input";
import { Popover, PopoverContent, PopoverTrigger } from "../UI/Popover";
import { GroupedTask, GroupedTasks } from "./TaskCard";
import { useEmployees } from "../../Contexts/EmployeeContext/EmployeeProvider";
import { IOrganization } from "../../Interfaces/Company";
import { filter } from "lodash";

export type TaskFilters = {
  title?: string;
  description?: string;
  employeeId?: number;
  dateFrom?: Dayjs;
  dateTo?: Dayjs;
  cardMode?: taskCardMode;
};

type TaskFilterProps = {
  onChange: (filters: TaskFilters) => void;
  setParentCardMode: React.Dispatch<SetStateAction<taskCardMode>>;
};

export enum taskCardMode {
  showOwnTasks = 1,
  showAllTaks = 2,
  showFinishedTasks = 3,
  showFinishedTasksOfUser = 4,
}
export default function TaskFilter({
  onChange,
  setParentCardMode,
}: TaskFilterProps) {
  const { user } = useUser();
  const [cardMode, setCardMode] = useState<taskCardMode>(
    taskCardMode.showOwnTasks
  );
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>();
  const { employees } = useEmployees();

  useEffect(() => {
    onChange({
      title,
      description,
      cardMode,
      employeeId: selectedEmployeeId,
    });
    setParentCardMode(cardMode);
  }, [title, description, cardMode, selectedEmployeeId]);

  useEffect(() => {
    if (
      cardMode === taskCardMode.showOwnTasks ||
      cardMode === taskCardMode.showFinishedTasksOfUser
    ) {
      setSelectedEmployeeId(undefined);
    }
  }, [cardMode]);

  return (
    <div className="flex">
      {user?.organizationRoleName === "Admin" ||
      user?.organizationRoleName === "Owner" ? (
        <div className="rounded-full flex gap-1">
          <div
            className={`rounded-full border ${
              cardMode === taskCardMode.showOwnTasks && "bg-brand-light"
            }`}
          >
            <Tooltip title="Meine Aufgaben">
              <IconButton
                onClick={() => setCardMode(taskCardMode.showOwnTasks)}
              >
                <PersonIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div
            className={`rounded-full border ${
              cardMode === taskCardMode.showAllTaks && "bg-brand-light"
            }`}
          >
            <Tooltip title="Alle Aufgaben">
              <IconButton onClick={() => setCardMode(taskCardMode.showAllTaks)}>
                <BusinessIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div
            className={`rounded-full border ${
              cardMode === taskCardMode.showFinishedTasks && "bg-brand-light"
            }`}
          >
            <Tooltip title="Fertiggestellte Aufgaben">
              <IconButton
                onClick={() => setCardMode(taskCardMode.showFinishedTasks)}
              >
                <Check />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      ) : (
        <div className="rounded-full flex gap-1">
          <div
            className={`rounded-full border ${
              cardMode === taskCardMode.showOwnTasks && "bg-brand-light"
            }`}
          >
            <Tooltip title="Meine Aufgaben">
              <IconButton
                onClick={() => setCardMode(taskCardMode.showOwnTasks)}
              >
                <PersonIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div
            className={`rounded-full border ${
              cardMode === taskCardMode.showFinishedTasks && "bg-brand-light"
            }`}
          >
            <Tooltip title="Fertiggestellte Aufgaben">
              <IconButton
                onClick={() => setCardMode(taskCardMode.showFinishedTasks)}
              >
                <Check />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}
      <div className="flex">
        <Popover>
          <PopoverTrigger>
            <IconButton>
              <FilterAltIcon />
            </IconButton>
          </PopoverTrigger>
          <PopoverContent>
            <div className="flex flex-col justify-center items-center gap-2">
              <div className="flex flex-row">
                <Input
                  placeholder="Titel"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <IconButton onClick={() => setTitle("")}>
                  <LucideX />
                </IconButton>
              </div>
              <div className="flex flex-row">
                <Input
                  placeholder="Beschreibung"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <IconButton onClick={() => setDescription("")}>
                  <LucideX />
                </IconButton>
              </div>
            </div>
            {(cardMode === taskCardMode.showAllTaks ||
              cardMode === taskCardMode.showFinishedTasks) && (
              <div className="flex flex-row w-full mt-2">
                <Select
                  label="Mitarbeiter"
                  sx={{ width: "100%" }}
                  value={selectedEmployeeId}
                  onChange={(e) =>
                    setSelectedEmployeeId(Number(e.target.value))
                  }
                >
                  {employees.map(
                    (employee) =>
                      employee.active && (
                        <MenuItem value={employee.id}>
                          {employee.username}
                        </MenuItem>
                      )
                  )}
                </Select>
                <IconButton onClick={() => setSelectedEmployeeId(0)}>
                  <LucideX />
                </IconButton>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export function filterTaskList(
  taskList: ITask[],
  setFilteredTaskList: React.Dispatch<React.SetStateAction<GroupedTask[]>>,
  filters: TaskFilters,
  user: User | undefined | null,
  companies: IOrganization[]
) {
  const companyNameMap: { [key: number]: string } = companies.reduce(
    (acc, company) => {
      acc[company.id!] = company.name ?? "";
      return acc;
    },
    {} as { [key: number]: string }
  );

  let filteredTaskList: GroupedTask[] = [];

  let groupedTasks = groupBy!(
    [
      ...taskList?.filter((element: ITaskDTO) => {
        return (
          filters.cardMode === taskCardMode.showAllTaks ||
          element.userIds?.includes(user?.userId!)
        );
      }),
    ],
    "customerOrganizationId"
  );

  console.log("groupedTasks", groupedTasks);

  Object.entries(groupedTasks).forEach(([key, value]) => {
    let groupToPush: GroupedTask = {
      companyName: companyNameMap[Number(key)],
      tasks: value as ITaskDTO[],
    };
    filteredTaskList.push(groupToPush);
  });

  console.log("filteredTaskList before filters", filteredTaskList);

  if (filters.cardMode) {
    filteredTaskList = filteredTaskList.map((group) => {
      const newGroup = { ...group };

      // Filter the tasks within this new group
      newGroup.tasks = group.tasks.filter((task) => {
        return filters.cardMode === taskCardMode.showOwnTasks
          ? task.userIds?.includes(user?.userId!) && !task.finished
          : filters.cardMode === taskCardMode.showAllTaks
          ? !task.finished
          : filters.cardMode === taskCardMode.showFinishedTasksOfUser
          ? task.userIds?.includes(user?.userId!) && task.finished
          : task.finished; // Assuming this is the default/else case
      });

      // Return the new group object with the filtered tasks
      return newGroup;
    });
  }

  console.log("filteredTaskList after card mode filter", filteredTaskList);

  if (filters.title) {
    filteredTaskList = filteredTaskList.map((group) => {
      const newGroup = { ...group };

      // Filter the tasks within this new group based on title
      newGroup.tasks = group.tasks.filter((task) =>
        task.title?.toLowerCase().includes(filters.title!.toLowerCase())
      );

      // Return the new group object with the filtered tasks
      return newGroup;
    });
  }

  if (filters.description) {
    filteredTaskList = filteredTaskList.map((group) => {
      const newGroup = { ...group };

      // Filter the tasks within this new group based on description
      newGroup.tasks = group.tasks.filter((task) =>
        task.description
          ?.toLowerCase()
          .includes(filters.description!.toLowerCase())
      );

      // Return the new group object with the filtered tasks
      return newGroup;
    });
  }

  if (filters.employeeId && filters.employeeId !== 0) {
    filteredTaskList = filteredTaskList.map((group) => {
      const newGroup = { ...group };

      // Filter the tasks within this new group based on employee ID
      newGroup.tasks = group.tasks.filter((task) =>
        task.userIds?.includes(filters.employeeId!)
      );

      // Return the new group object with the filtered tasks
      return newGroup;
    });
  }

  filteredTaskList = filteredTaskList.map((group) => ({
    ...group,
    tasks: group.tasks.sort((a, b) => {
      const titleA = a.title || ""; // Handle potential null/undefined title
      const titleB = b.title || ""; // Handle potential null/undefined title
      return titleA.localeCompare(titleB);
    }),
  }));

  //Sort the groups themselves by companyName
  filteredTaskList = filteredTaskList.sort((a, b) => {
    const nameA = a.companyName || ""; // Handle potential null/undefined companyName
    const nameB = b.companyName || ""; // Handle potential null/undefined companyName
    return nameA.localeCompare(nameB);
  });

  console.log("filteredTaskList", filteredTaskList);

  setFilteredTaskList(filteredTaskList);
}
